import {Injectable, InjectionToken } from '@angular/core';
import {
  createQueryParams,
  IImpersonationResponse,
  IPaginatedResponse,
  IRepresentative,
  IRepresentativeRequest,
  IUserQueryParams, IUserStats, IUserStatusBulkRequest, IUserStatusBulkResponse,
  PermissionType,
  UpdateRepresentativePassword,
  UserType,
} from '@inshuwa-client/models';
import { Observable } from 'rxjs';
import { RepresentativeService } from './representative.service';
import { HttpClient } from '@angular/common/http';
import { baseUrl } from '../configs/baseUrl';
import { environment } from '../environments/environment';
import {NgHttpCachingHeaders} from "ng-http-caching";

export interface IUsersService {
  getUsers(
    queryParams?: Partial<IUserQueryParams>,
  ): Observable<IPaginatedResponse<IRepresentative>>;

  addUser(
    queryParams?: Partial<IRepresentativeRequest>,
  ): Observable<IRepresentative>;
}

export const IUsersServiceInjectionToken = new InjectionToken<IUsersService>(
  'users',
);

@Injectable({
  providedIn: 'root',
})
export class UserService {


  constructor(
    private rep: RepresentativeService,
    private http: HttpClient,
  ) {}

  getUsers(
    params: Partial<IUserQueryParams>,
    noCache: boolean = false,
  ): Observable<IPaginatedResponse<IRepresentative>> {
    const HttpCatchingHeaders = noCache ? NgHttpCachingHeaders.DISALLOW_CACHE : NgHttpCachingHeaders.ALLOW_CACHE
    return this.http.get<IPaginatedResponse<IRepresentative>>(
      `${baseUrl()}/users`,
      {
        params: createQueryParams(params),
        headers: {
          [HttpCatchingHeaders]: '1'
        }
      },
    );
  }

  getUsersStats(
    params: Partial<IUserQueryParams>,
    noCache: boolean = false,
  ): Observable<IUserStats> {
    const HttpCatchingHeaders = noCache ? NgHttpCachingHeaders.DISALLOW_CACHE : NgHttpCachingHeaders.ALLOW_CACHE
    return this.http.get<IUserStats>(
      `${baseUrl()}/users/stats`,
      {
        params: createQueryParams(params),
        headers: {
          [HttpCatchingHeaders]: '1'
        }
      },
    );
  }

  getUser(id: number): Observable<IRepresentative> {
    return this.http.get<IRepresentative>(
      `${baseUrl()}/users/${id}`
    );
  }

  changePassword(
    request: UpdateRepresentativePassword,
  ): Observable<UpdateRepresentativePassword> {
    return this.http.put<UpdateRepresentativePassword>(
      `${baseUrl()}/auth/password/change`,
      request,
    );
  }

  editUser(
    id: number,
    request: Partial<IRepresentativeRequest>,
  ): Observable<IRepresentative> {
    return this.http.put<IRepresentative>(
      `${baseUrl()}/users/${id}`,
      request,
    );
  }

  deleteUser(id: number) {
    return this.http.delete(`${baseUrl()}/users/${id}`);
  }

  getAdminPermissions(userId: number, noCache: boolean = true): Observable<PermissionType> {
    const HttpCatchingHeaders = noCache ? NgHttpCachingHeaders.DISALLOW_CACHE : NgHttpCachingHeaders.ALLOW_CACHE
    return this.http.get<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/admin`,
      {
        headers: {
          [HttpCatchingHeaders]: '1',
        }
      }
    );
  }

  getIntermediaryPermissions(userId: number, noCache: boolean = true): Observable<PermissionType> {
    const HttpCatchingHeaders = noCache ? NgHttpCachingHeaders.DISALLOW_CACHE : NgHttpCachingHeaders.ALLOW_CACHE
    return this.http.get<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/intermediary`,
      {
        headers: {
          [HttpCatchingHeaders]: '1',
        }
      }
    );
  }

  getPartnerPermissions(userId: number, noCache: boolean = true): Observable<PermissionType> {
    const HttpCatchingHeaders = noCache ? NgHttpCachingHeaders.DISALLOW_CACHE : NgHttpCachingHeaders.ALLOW_CACHE
    return this.http.get<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/partner`,
      {
        headers: {
          [HttpCatchingHeaders]: '1',
        }
      }
    );
  }

  getInsurerPermissions(userId: number, noCache: boolean = true): Observable<PermissionType> {
    const HttpCatchingHeaders = noCache ? NgHttpCachingHeaders.DISALLOW_CACHE : NgHttpCachingHeaders.ALLOW_CACHE
    return this.http.get<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/insurer`,
      {
        headers: {
          [HttpCatchingHeaders]: '1',
        }
      }
    );
  }

  updateParnterPermissions(
    userId: number,
    request: Partial<PermissionType>,
  ): Observable<PermissionType> {
    return this.http.put<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/partner`,
      request,
    );
  }

  updateInsurerPermissions(
    userId: number,
    request: Partial<PermissionType>,
  ): Observable<PermissionType> {
    return this.http.put<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/insurer`,
      request,
    );
  }

  updateIntermediaryPermissions(
    userId: number,
    request: Partial<PermissionType>,
  ): Observable<PermissionType> {
    return this.http.put<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/intermediary`,
      request,
    );
  }

  updateAdminPermissions(
    userId: number,
    request: Partial<PermissionType>,
  ): Observable<PermissionType> {
    return this.http.put<PermissionType>(
      `${baseUrl()}/users/${userId}/permissions/admin`,
      request,
    );
  }

  private hasRole(roles: string[], userRoles: string[]): boolean {
    return roles.some(role => (userRoles ?? []).includes(role));
  }

  userPermissions(action: 'get' | 'update' | 'add' = 'get', user: IRepresentative, payload: Partial<PermissionType> | null = null) {
    const partner = ['PartnerRepresentative', 'AdminPartnerRepresentative']
    const insurer = ['InsurerRepresentative']
    const intermediaryRepresentatives = ['BrokerRepresentative', 'AgentRepresentative', 'AdminAgentRepresentative'];
    const adminRepresentatives = ['Admin'];

    const isAdminPartners = this.hasRole(adminRepresentatives, user.roles)
    const isIntermediary = this.hasRole(intermediaryRepresentatives, user.roles)
    const isInsurer = this.hasRole(insurer, user.roles)
    const isPartner = this.hasRole(partner, user.roles)

    switch (true) {
      case isAdminPartners:
        switch(action) {
          case "update":
            return payload ? this.updateAdminPermissions(user.id, payload) : null;
          default:
            return this.getAdminPermissions(user.id);
        }

      case isIntermediary:
        switch(action) {
          case "update":
            return payload ? this.updateIntermediaryPermissions(user.id, payload) : null;
          default:
            return this.getIntermediaryPermissions(user.id);
        }

      case isInsurer:
        switch(action) {
          case "update":
            return payload ? this.updateInsurerPermissions(user.id, payload) : null;
          default:
            return this.getInsurerPermissions(user.id);
        }

      case isPartner:
        switch(action) {
          case "update":
            return payload ? this.updateParnterPermissions(user.id, payload) : null;
          default:
            return this.getPartnerPermissions(user.id);
        }

      default:
        return null
    }
  }

  deactivateUsers(
    request: IUserStatusBulkRequest
  )  {
    return this.http.put<IUserStatusBulkResponse>(
      baseUrl() + `/users/bulk`,
      request
    )
  }

  getPermissionsSchema(userType: UserType): Observable<Record<string, Record<string, boolean>>> {
    return this.http.get<Record<string, Record<string, boolean>>>(
      `${baseUrl()}/auth/permissions/${userType}`,
    );
  }

  addUserClaim(userId: number, claimType: string, claimValue: 'true' | 'false'): Observable<{ status: boolean; message: string }> {
    return this.http.post<{ status: boolean; message: string }>(
      `${baseUrl()}/auth/users/${userId}/claims`,
      { claimType, claimValue },
    );
  }

  removeUserClaim(userId: number, claimType: string): Observable<{ status: boolean; message: string }> {
    return this.http.delete<{ status: boolean; message: string }>(
      `${baseUrl()}/auth/users/${userId}/claims/${claimType}`,
    );
  }

  impersonateUser(
    userId: number,
    insurerId: number | null = environment.insurerId ?? null,
  ): Observable<IImpersonationResponse> {
    return this.http
      .post<IImpersonationResponse>(`${baseUrl()}/auth/impersonate`, {
        userId,
        insurerId,
      });
  }
}
